import { Component, ContentChild, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { onConfirmDialog } from '../../../../../core/helpers/helpers.function';
import { ICountry } from '../../../../../core/models/country.interface';
import { NotificationService } from '../../../../../core/services/notification.service';
import { UserService } from '../../../../../core/services/user.service';
import { ConfirmPopupDialog } from '../../../../../shared/popups/confirm-popup/confirm-popup.component';
import { ISubCarrierPrice } from '../../../models/shipment-price.interface';

@Component({
    selector: 'eci-table-pricing-sub-carriers',
    templateUrl: './table-pricing-sub-carriers.component.html',
    styleUrls: [ './table-pricing-sub-carriers.component.scss' ],
})

export class TablePricingSubCarriersComponent implements OnInit {
    @Input() public dataSource: MatTableDataSource<ISubCarrierPrice>;
    @Input() public displayedColumns: string[];
    @Input() public dynamicColumns: string[] = [];
    @Input() public shipmentPriceItemId: number;
    @Input() public theCountriesList: ICountry[];
    @Input() public isCreateBtn: boolean = true;

    @Output() public sortRows: EventEmitter<Sort> = new EventEmitter<Sort>(undefined);
    @Output() public onEdit: EventEmitter<any> = new EventEmitter<any>(null);
    @Output() public onCancel: EventEmitter<any> = new EventEmitter<any>(null);
    @Output() public onSave: EventEmitter<any> = new EventEmitter<any>(null);
    @Output() public onDelete: EventEmitter<any> = new EventEmitter<any>(null);
    @Output() public onNewRow: EventEmitter<any> = new EventEmitter<any>(null);

    @ViewChild(MatSort) public sort: MatSort;
    @ContentChild('controlTemplate') public controlTemplate: TemplateRef<any>;
    @ContentChild('paginatorTemplate') public paginatorTemplate: TemplateRef<any>;

    public isEditMode: boolean | number = false;
    public addNewRowDataBtn: boolean = false;
    public form: FormGroup;
    public countriesList: ICountry[] = [];

    constructor(
        public userSvc: UserService,
        private dialog: MatDialog,
        private notify: NotificationService,
    ) {}

    ngOnInit(): void {
        this.countriesList = this.theCountriesList;
        this.form = this.initializeForm();
    }

    private initializeForm(data?: ISubCarrierPrice): FormGroup {
        return new FormGroup({
            destination:  new FormControl({disabled: false, value: data?.destination || undefined},
                [ Validators.required ]),
            costNetRates: new FormControl({disabled: false, value: data?.costNetRates || ''},
                [ Validators.required, Validators.min(0.01) ]),
            margin:  new FormControl({disabled: false, value: data?.margin || ''},
                [ Validators.required, Validators.min(0.01) ]),
            sellPrice:  new FormControl({disabled: false, value: data?.sellPrice || ''},
                [ Validators.required, Validators.min(0.01) ]),
            unit:  new FormControl({disabled: false, value: data?.unit || ''},
                [ Validators.required ]),
            subCarrier: new FormControl({disabled: false, value: data?.subCarrier || ''},
                [ /*Validators.required,*/ Validators.minLength(1), Validators.maxLength(100) ]),
            currency:  new FormControl({disabled: false, value: data?.currency || 'EUR'},
                [ Validators.required, Validators.minLength(1), Validators.maxLength(10) ]),
            shipmentPriceItemId: new FormControl({disabled: false, value: data?.shipmentPriceItemId || this?.shipmentPriceItemId},
                [ Validators.required ]),
            searchCountry: new FormControl(''),
            prices: new FormGroup(this.generateDynamicFields(data)),
        });
    }

    private generateDynamicFields(shipmentPrice): {} {
        const group = {};
        this.dynamicColumns?.forEach((colName, index) => {
            group[colName] = new FormControl(this.getCost(shipmentPrice, colName));
        });
        return group;
    }

    private convertToMoney(value: any): any {
        return parseFloat(value || 0).toFixed(2);
    }

    private removeData(index: number): void {
        this.onCancel.emit(index);
    }

    public sortData(e: Sort): void {
        this.sortRows.emit(e);
    }

    public convertToPrice(value: any): any {
        if (value === undefined || value === null) {
            return '';
        }
        return this.convertToMoney(value);
    }

    public convertToPercentage(value: any): any {
        if (value === undefined || value === null) {
            return '';
        }
        return this.convertToMoney(value) + '%';
    }

    public save(event: MouseEvent, element, index): void {
        event.stopPropagation();
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.notify.showError('Not all fields are filled');
            return;
        }
        this.onSave.emit({
            form: this.form.value, element, onSuccess: () => {
                this.isEditMode = false;
                this.addNewRowDataBtn = false;
            }, index
        });
    }

    public close(event: MouseEvent, element, index): void {
        event.stopPropagation();
        const onConfirm = () => {
            if (!element.id) {
                this.removeData(index);
            }
            this.isEditMode = false;
            this.addNewRowDataBtn = false;
        };
        const dialogRef = this.dialog.open(ConfirmPopupDialog);
        dialogRef.componentInstance.message = `Are you sure you want to cancel Edit action.`;
        dialogRef.componentInstance.rightBtnText = 'Yes';
        dialogRef.componentInstance.leftBtnText = 'No';
        dialogRef.afterClosed().subscribe(value => {
            if (!value) {
                return;
            }
            onConfirm();
        });
    }

    public delete(event: MouseEvent, id, index): void {
        event.stopPropagation();
        const onConfirm = () => {
            this.onDelete.emit({ id, index });
            this.isEditMode = false;
            this.addNewRowDataBtn = false;
        };
        const dialogRef = this.dialog.open(ConfirmPopupDialog);
        onConfirmDialog(dialogRef, onConfirm, 'entity');
    }

    public edit(event: MouseEvent, element, index) {
        event.stopPropagation();
        const currentData: ISubCarrierPrice = {
            costNetRates: element.costNetRates,
            weight: element.weight,
            margin: element.margin,
            sellPrice: element.sellPrice,
            unit: element.unit,
            subCarrier: element.subCarrier,
            currency: element.currency,
            destination: this.getCountryDataByDestination(element.destination)?.id,
            shipmentPriceItemId: element.shipmentPriceItemId || this.shipmentPriceItemId,
            prices: element.prices,
        };
        this.form = this.initializeForm(currentData);
        this.form.patchValue(currentData);
        this.isEditMode = element.id;
        this.addNewRowDataBtn = true;
    }

    public addNewRow(): void {
        this.addNewRowDataBtn = true;
        const newData: ISubCarrierPrice = {
            shipmentPriceItem: undefined,
            costNetRates: undefined,
            weight: undefined,
            margin: undefined,
            sellPrice: undefined,
            unit: undefined,
            subCarrier: undefined,
            currency: undefined,
            destination: undefined,
            shipmentPriceItemId: this.shipmentPriceItemId || this.shipmentPriceItemId,
            prices: [],
        };
        this.isEditMode = undefined;
        this.form = this.initializeForm(newData);
        this.onNewRow.emit(newData);
    }

    public get isAdmin(): boolean {
        return this.userSvc.isAdmin;
    }

    public getCorrectCountryByDestination(destination: any): string {
        const selectedCountry = this.getCountryDataByDestination(destination);
        return (selectedCountry ? `${selectedCountry.name}(${selectedCountry.code})` : undefined);
    }

    protected getCountryDataByDestination(destination: any): ICountry {
        return (this.theCountriesList?.find((country) => (+country.id === +destination ||
                country.code === destination || country.name === destination || country.codeFull === destination))
            || undefined);
    }

    public compareCountries = (o1: any, o2: any) => {
        if (!o1 || !o2) {
            return false;
        }
        return (+o1 === +o2);
    }

    public getCost(element, disCol: string): string {
        const priceByZone = element?.prices?.find((item) => item.name === disCol);
        return priceByZone?.cost || '';
    }
    public isInputExist(element, disCol: string): boolean {
        return !!element?.prices.findIndex((item) => item.name === disCol);
    }
}
